import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { PagingResponse } from '@models/paging-response.model';
import { catchError, map } from 'rxjs/operators';
import { HttpClient, HttpParams } from '@angular/common/http';
import { AppConfigService } from '../services/app-config.service';
import { ErrorHandlerService } from '../services/error-handler.service';
import { CaseNote } from '../models/ui/case-note.model';
import { DocNoteResource } from '../models/resource/doc-note.resource';
import { DocNoteResourceToCaseNoteMapper } from '../models/mappers/doc-note-resource-to-case-note.mapper';
import { CaseNoteToDocNoteResourceMapper } from '../models/mappers/case-note-to-doc-note-resource.mapper';

const docNoteResourceToCaseNoteMapper = new DocNoteResourceToCaseNoteMapper();
const caseNoteToDocNoteResourceMapper = new CaseNoteToDocNoteResourceMapper();

@Injectable({ providedIn: 'root' })
export class DocNotesSdk {
	constructor(private http: HttpClient, private appConfig: AppConfigService, private errorHandler: ErrorHandlerService) { }

	public get(params?: DocNotesGetParams): Observable<CaseNote[]> {
		let requestParams = new HttpParams();

		if (params?.id)
			return this.http.get<PagingResponse<DocNoteResource>>(`${this.appConfig.getConfig().webApi}/api2/DocNotes/${params.id}`).pipe(
				map((response) => docNoteResourceToCaseNoteMapper.mapCollection(response?.value)),
				catchError(this.errorHandler.handleError<CaseNote[]>(`[GET] /api2/DocNotes/[docNoteId]`))
			);

		if (params?.searchTerm) {
			requestParams = requestParams.set('$search', params.searchTerm.toLowerCase());
		}

		if (params?.take) {
			requestParams = requestParams.set('$top', params?.take);
		}

		requestParams = requestParams.set('$count', true);

		return this.http.get<PagingResponse<DocNoteResource>>(`${this.appConfig.getConfig().webApi}/api2/DocNotes`, { params: requestParams }).pipe(
			map((response) => docNoteResourceToCaseNoteMapper.mapCollection(response?.value)),
			catchError(this.errorHandler.handleError<CaseNote[]>(`[GET] /api2/DocNotes`))
		);
	}

	public post(params: DocNotesPostParams): Observable<CaseNote> {
		const docNote = caseNoteToDocNoteResourceMapper.map(params.note);
		return this.http.post<DocNoteResource>(`${this.appConfig.getConfig().webApi}/api2/DocNotes`, docNote)
			.pipe(
				map((response) => docNoteResourceToCaseNoteMapper.map(response)),
				catchError(this.errorHandler.handleError<CaseNote>(`[POST] /api2/DocNotes`))
			);
	}

	public put(params: DocNotesPutParams): Observable<CaseNote> {
		const docNote = caseNoteToDocNoteResourceMapper.map(params.note);
		return this.http.put<DocNoteResource>(`${this.appConfig.getConfig().webApi}/api2/DocNotes/${docNote.docNoteId}`, docNote)
			.pipe(
				map((response) => docNoteResourceToCaseNoteMapper.map(response)),
				catchError(this.errorHandler.handleError<CaseNote>(`[PUT] /api2/DocNotes/${docNote.docNoteId}`))
			);
	}
}

interface DocNotesGetParams {
	searchTerm?: string;
	take?: number;
	id?: string;
}

interface DocNotesPostParams {
	note: CaseNote;
}

interface DocNotesPutParams {
	note: CaseNote;
}
